import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  CheckCircle2,
  DollarSign,
  Info,
  ShieldCheck,
} from "lucide-react";

export default function PricingBlock() {
  // Estado para alternar entre Pronto Pago (early) y Tarifa Completa (full)
  const [billingCycle, setBillingCycle] = useState("early");

  const pricingData = [
    {
      category: "Estudiantes de grado y posgrado",
      condition: "Sin ponencia",
      earlyPrice: "25.00",
      fullPrice: "45.00",
      type: "student",
    },
    {
      category: "Estudiantes de grado y posgrado",
      condition: "Con ponencia o póster",
      earlyPrice: "35.00",
      fullPrice: "55.00",
      type: "student-speaker",
    },
    {
      category: "Profesionales",
      condition: "Con ponencia",
      earlyPrice: "60.00",
      fullPrice: "80.00",
      type: "professional",
    },
    {
      category: "Profesionales",
      condition: "Sin ponencia",
      earlyPrice: "55.00",
      fullPrice: "75.00",
      type: "professional",
    },
    {
      category: "Miembros de la ACEI",
      condition: "Con ponencia",
      earlyPrice: "50.00",
      fullPrice: "70.00",
      type: "member",
    },
    {
      category: "Miembros de la ACEI",
      condition: "Sin ponencia",
      earlyPrice: "45.00",
      fullPrice: "65.00",
      type: "member",
    },
  ];

  return (
    <div className="bg-white text-slate-800 py-16 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-5xl mx-auto">
        {/* Encabezado */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight sm:text-4xl">
            Inscripciones{" "}
          </h2>
       <p className="mt-3 max-w-2xl mx-auto text-xl text-red-500 sm:mt-4">
            Próximamente inscripciones
          </p>
          {/* Selector de periodo (Tabs) */}
          <div className="mt-10 flex justify-center">
            <div className="relative flex bg-slate-200 p-1 rounded-xl w-full max-w-md">
              {/* Fondo animado del tab activo */}
              <motion.div
                className="absolute top-1 bottom-1 left-1 bg-white rounded-lg shadow-sm"
                layoutId="activeTab"
                transition={{ type: "spring", stiffness: 300, damping: 300 }}
                style={{
                  width: "calc(50% - 4px)",
                  transform:
                    billingCycle === "early"
                      ? "translateX(0%)"
                      : "translateX(100%)",
                }}
              />

              <button
                type="button"
                onClick={() => setBillingCycle("early")}
                className={`relative w-1/2 rounded-lg py-2.5 text-sm font-semibold tracking-wide transition-colors duration-200 focus:outline-none ${
                  billingCycle === "early"
                    ? "text-blue-600"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Pronto pago
                <span className="block text-xs font-normal text-slate-400">
                  Hasta el 15 oct 2026
                </span>
              </button>

              <button
                type="button"
                onClick={() => setBillingCycle("full")}
                className={`relative w-1/2 rounded-lg py-2.5 text-sm font-semibold tracking-wide transition-colors duration-200 focus:outline-none ${
                  billingCycle === "full"
                    ? "text-blue-600"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Tarifa completa
                <span className="block text-xs font-normal text-slate-400">
                  Desde el 16 oct 2026
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Grid de Tarjetas de Costos */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <AnimatePresence mode="popLayout">
            {pricingData.map((item, index) => {
              const currentPrice =
                billingCycle === "early" ? item.earlyPrice : item.fullPrice;
              const isSpeaker = item.condition.includes("Con ponencia");

              return (
                <motion.div
                  key={`${item.category}-${item.condition}-${billingCycle}`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className={`bg-white rounded-2xl p-6 shadow-sm border transition-all duration-200 flex flex-col justify-between ${
                    isSpeaker
                      ? "border-blue-200 bg-blue-50/10 ring-1 ring-blue-100"
                      : "border-slate-200"
                  }`}
                >
                  <div>
                    {/* Etiqueta de la categoría */}
                    <div className="flex items-center justify-between mb-4">
                      <span
                        className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-md ${
                          item.type.includes("student")
                            ? "bg-amber-100 text-amber-800"
                            : item.type === "member"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-slate-100 text-slate-800"
                        }`}
                      >
                        {item.category}
                      </span>
                      {isSpeaker && (
                        <span className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
                          <CheckCircle2 size={12} /> Ponente
                        </span>
                      )}
                    </div>

                    {/* Especificación / Condición */}
                    <h3 className="text-lg font-semibold text-slate-900 mb-6">
                      {item.condition}
                    </h3>

                    {/* Contenedor del Precio */}
                    <div className="flex items-baseline text-slate-950 mb-4">
                      <span className="text-xl font-medium tracking-tight text-slate-400">
                        USD
                      </span>
                      <span className="text-4xl font-extrabold tracking-tight ml-1">
                        {currentPrice}
                      </span>
                    </div>
                  </div>

                  {/* Detalle inferior */}
                  <div className="pt-4 border-t border-slate-100 mt-4 flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {billingCycle === "early"
                        ? "Vence: 15 de Oct"
                        : "Evento: 26-27 Nov"}
                    </span>
                 
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {/* Notas aclaratorias importantes */}
        <div className="mt-12 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col sm:flex-row gap-4 items-start">
          <div className="p-3 bg-blue-50 rounded-xl text-blue-600 shrink-0">
            <Info size={24} />
          </div>
          <div className="text-sm text-slate-600 space-y-2">
            <h4 className="font-bold text-slate-900 text-base">
              Información importante sobre el registro
            </h4>
            <p className="text-slate-500">
              El periodo de <strong>Pronto Pago</strong> representa un beneficio
              exclusivo de ahorro programado aplicable únicamente hasta las
              fechas estipuladas. A partir del 16 de octubre de 2026, sin
              excepción, entrará en vigencia el esquema de{" "}
              <strong>Tarifa Completa</strong>.
            </p>
            <p className="flex items-center gap-1 text-xs font-medium text-slate-500">
              <ShieldCheck size={14} className="text-emerald-500" /> Miembros de
              la ACEI deben presentar certificación vigente para validar el
              descuento en taquilla o plataforma.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
